import React, { useEffect, useState } from "react";
import axios from "axios";
import AdminAPI from "../utils/adminApi";

const ProofAPI = axios.create({
  baseURL: "https://alkebulanfkalertbackend.onrender.com/api/payment-proof",
  headers: { "Content-Type": "application/json" },
});
ProofAPI.interceptors.request.use((req) => {
  const token = localStorage.getItem("adminToken");
  if (token) req.headers.Authorization = `Bearer ${token}`;
  return req;
});

const OrderAPI = axios.create({
  baseURL: "https://alkebulanfkalertbackend.onrender.com/api/orders",
  headers: { "Content-Type": "application/json" },
});
OrderAPI.interceptors.request.use((req) => {
  const token = localStorage.getItem("adminToken");
  if (token) req.headers.Authorization = `Bearer ${token}`;
  return req;
});

const SupportAPI = axios.create({
  baseURL: "https://alkebulanfkalertbackend.onrender.com/api/support",
  headers: { "Content-Type": "application/json" },
});
SupportAPI.interceptors.request.use((req) => {
  const token = localStorage.getItem("adminToken");
  if (token) req.headers.Authorization = `Bearer ${token}`;
  return req;
});

const AdminUsers = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedProof, setSelectedProof] = useState(null);
  const [rejectModal, setRejectModal] = useState({ open: false, userId: null });
  const [rejectReason, setRejectReason] = useState("");

  const [proofs, setProofs] = useState([]);
  const [proofsLoading, setProofsLoading] = useState(true);
  const [proofActionId, setProofActionId] = useState(null);
  const [proofRejectModal, setProofRejectModal] = useState({ open: false, proofId: null });
  const [proofRejectNote, setProofRejectNote] = useState("");

  const [detailUser, setDetailUser] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [resetPasswordResult, setResetPasswordResult] = useState(null);
  const [detailActionLoading, setDetailActionLoading] = useState(false);
  const [detailCreditAmount, setDetailCreditAmount] = useState("");
  const [detailDebitAmount, setDetailDebitAmount] = useState("");
  const [detailMode, setDetailMode] = useState("credit");
  const [debitConfirmOpen, setDebitConfirmOpen] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);

  const [pendingOrderCounts, setPendingOrderCounts] = useState({});
  const [userOrders, setUserOrders] = useState([]);
  const [userOrdersLoading, setUserOrdersLoading] = useState(false);
  const [orderActionId, setOrderActionId] = useState(null);

  const [cleanupLoading, setCleanupLoading] = useState(false);
  const [cleanupDone, setCleanupDone] = useState(false);

  const [tickets, setTickets] = useState([]);
  const [ticketsLoading, setTicketsLoading] = useState(true);
  const [ticketActionId, setTicketActionId] = useState(null);
  const [ticketReplyModal, setTicketReplyModal] = useState({ open: false, ticketId: null });
  const [ticketReplyText, setTicketReplyText] = useState("");

  const [searchQuery, setSearchQuery] = useState("");

  // ✅ bulk-select/delete state
  const [selectedUserIds, setSelectedUserIds] = useState([]);
  const [bulkDeleteConfirmOpen, setBulkDeleteConfirmOpen] = useState(false);
  const [bulkDeleteLoading, setBulkDeleteLoading] = useState(false);

  // ✅ NEW: confirmation modal state for deactivating a subscription
  const [deactivateConfirmOpen, setDeactivateConfirmOpen] = useState(false);

  const fetchUsers = async () => {
    try {
      const res = await AdminAPI.get("/users");
      setUsers(res.data);
    } catch (err) {
      console.error("❌ Error fetching users", err.response?.data || err.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchProofs = async () => {
    try {
      const res = await ProofAPI.get("/", { params: { status: "pending" } });
      setProofs(res.data);
    } catch (err) {
      console.error("❌ Error fetching payment proofs", err.response?.data || err.message);
    } finally {
      setProofsLoading(false);
    }
  };

  const fetchPendingOrderCounts = async () => {
    try {
      const res = await OrderAPI.get("/pending-counts");
      const map = {};
      res.data.forEach((entry) => {
        map[entry._id] = entry.count;
      });
      setPendingOrderCounts(map);
    } catch (err) {
      console.error("❌ Error fetching pending order counts", err.response?.data || err.message);
    }
  };

  const fetchTickets = async () => {
    try {
      const res = await SupportAPI.get("/tickets");
      setTickets(res.data);
    } catch (err) {
      console.error("❌ Error fetching tickets", err.response?.data || err.message);
    } finally {
      setTicketsLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
    fetchProofs();
    fetchPendingOrderCounts();
    fetchTickets();
    const interval = setInterval(() => {
      fetchUsers();
      fetchProofs();
      fetchPendingOrderCounts();
      fetchTickets();
    }, 10000);
    return () => clearInterval(interval);
  }, []);

  const verifyUser = async (id) => {
    try {
      await AdminAPI.patch(`/users/${id}/verify`);
      fetchUsers();
    } catch (err) {
      console.error("❌ Error approving user", err.response?.data || err.message);
    }
  };

  const rejectUser = async () => {
    if (!rejectReason.trim()) return alert("Please provide a reason.");
    try {
      await AdminAPI.patch(`/users/${rejectModal.userId}/reject`, {
        reason: rejectReason,
      });
      setRejectModal({ open: false, userId: null });
      setRejectReason("");
      fetchUsers();
    } catch (err) {
      console.error("❌ Error rejecting user", err.response?.data || err.message);
    }
  };

  const toggleLockBalance = async (userId, isLocked, fromModal = false) => {
    try {
      const action = !isLocked ? "lock" : "unlock";
      const reason = !isLocked
        ? prompt("Enter reason for locking this user's balance:")
        : "";
      if (!isLocked && reason === null) return;
      await AdminAPI.patch(`/users/${userId}/lock-balance`, {
        locked: !isLocked,
        reason,
      });
      alert(`User balance ${action}ed successfully`);
      fetchUsers();
      if (fromModal) await refreshDetailUser();
    } catch (err) {
      console.error("❌ Error toggling lock", err.response?.data || err.message);
      alert(err.response?.data?.message || "Failed to update lock status");
    }
  };

  // 🚫 Deactivate / reactivate a whole account. When deactivated, the
  // user cannot log in — the login page shows them the reason.
  const toggleAccountLock = async (userId, isLocked, fromModal = false) => {
    try {
      if (!isLocked) {
        const reason = prompt(
          "Enter the reason for deactivating this account.\n\n(The user will see this message when they try to log in.)"
        );
        if (reason === null) return; // cancelled
        if (!reason.trim()) {
          alert("A reason is required — the user will see it at login.");
          return;
        }
        await AdminAPI.patch(`/users/${userId}/lock-account`, {
          locked: true,
          reason: reason.trim(),
        });
        alert("🚫 Account deactivated. The user will see your reason at login.");
      } else {
        if (!window.confirm("Reactivate this account? The user will be able to log in again.")) return;
        await AdminAPI.patch(`/users/${userId}/lock-account`, {
          locked: false,
          reason: "",
        });
        alert("✅ Account reactivated.");
      }
      fetchUsers();
      if (fromModal) await refreshDetailUser();
    } catch (err) {
      console.error("❌ Error toggling account lock", err.response?.data || err.message);
      alert(err.response?.data?.message || "Failed to update account status");
    }
  };

  const approveProof = async (proofId) => {
    setProofActionId(proofId);
    try {
      const res = await ProofAPI.patch(`/${proofId}/approve`);
      alert(res.data.message);
      fetchProofs();
      fetchUsers();
    } catch (err) {
      console.error("❌ Error approving proof", err.response?.data || err.message);
      alert(err.response?.data?.message || "Failed to approve proof");
    } finally {
      setProofActionId(null);
    }
  };

  const rejectProof = async () => {
    if (!proofRejectModal.proofId) return;
    setProofActionId(proofRejectModal.proofId);
    try {
      await ProofAPI.patch(`/${proofRejectModal.proofId}/reject`, {
        adminNote: proofRejectNote,
      });
      setProofRejectModal({ open: false, proofId: null });
      setProofRejectNote("");
      fetchProofs();
    } catch (err) {
      console.error("❌ Error rejecting proof", err.response?.data || err.message);
      alert(err.response?.data?.message || "Failed to reject proof");
    } finally {
      setProofActionId(null);
    }
  };

  const resolveTicket = async (ticketId) => {
    setTicketActionId(ticketId);
    try {
      const res = await SupportAPI.patch(`/tickets/${ticketId}/resolve`, {});
      alert(res.data.message);
      fetchTickets();
    } catch (err) {
      console.error("❌ Error resolving ticket", err.response?.data || err.message);
      alert(err.response?.data?.message || "Failed to resolve ticket");
    } finally {
      setTicketActionId(null);
    }
  };

  const resolveTicketWithReply = async () => {
    if (!ticketReplyModal.ticketId) return;
    setTicketActionId(ticketReplyModal.ticketId);
    try {
      const res = await SupportAPI.patch(`/tickets/${ticketReplyModal.ticketId}/resolve`, {
        adminReply: ticketReplyText,
      });
      alert(res.data.message);
      setTicketReplyModal({ open: false, ticketId: null });
      setTicketReplyText("");
      fetchTickets();
    } catch (err) {
      console.error("❌ Error resolving ticket", err.response?.data || err.message);
      alert(err.response?.data?.message || "Failed to resolve ticket");
    } finally {
      setTicketActionId(null);
    }
  };

  const openUserDetail = async (userId) => {
    setDetailLoading(true);
    setResetPasswordResult(null);
    setDetailCreditAmount("");
    setDetailDebitAmount("");
    setDetailMode("credit");
    setUserOrders([]);
    try {
      const res = await AdminAPI.get(`/users/${userId}`);
      setDetailUser(res.data);
      fetchUserOrders(userId);
    } catch (err) {
      console.error("❌ Error fetching user detail", err.response?.data || err.message);
      alert(err.response?.data?.message || "Failed to load user detail");
    } finally {
      setDetailLoading(false);
    }
  };

  const fetchUserOrders = async (userId) => {
    setUserOrdersLoading(true);
    try {
      const res = await OrderAPI.get(`/user/${userId}`);
      setUserOrders(res.data);
    } catch (err) {
      console.error("❌ Error fetching user orders", err.response?.data || err.message);
    } finally {
      setUserOrdersLoading(false);
    }
  };

  const closeUserDetail = () => {
    setDetailUser(null);
    setResetPasswordResult(null);
    setDebitConfirmOpen(false);
    setDeleteConfirmOpen(false);
    setDeactivateConfirmOpen(false);
    setUserOrders([]);
  };

  const refreshDetailUser = async () => {
    if (!detailUser?._id) return;
    try {
      const res = await AdminAPI.get(`/users/${detailUser._id}`);
      setDetailUser(res.data);
    } catch (err) {
      console.error("Refresh detail failed:", err.message);
    }
  };

  const approveOrder = async (orderId) => {
    setOrderActionId(orderId);
    try {
      const res = await OrderAPI.post(`/${orderId}/admin-confirm`);
      alert(res.data.message);
      await fetchUserOrders(detailUser._id);
      await refreshDetailUser();
      fetchUsers();
      fetchPendingOrderCounts();
    } catch (err) {
      console.error("❌ Error approving order", err.response?.data || err.message);
      alert(err.response?.data?.message || "Failed to approve order");
    } finally {
      setOrderActionId(null);
    }
  };

  const handleDetailCredit = async () => {
    const amount = Number(detailCreditAmount);
    if (!amount || amount <= 0) return alert("Enter a valid amount to credit.");
    setDetailActionLoading(true);
    try {
      await AdminAPI.post("/credit", { email: detailUser.email, amount });
      setDetailCreditAmount("");
      await refreshDetailUser();
      fetchUsers();
      alert(`✅ Credited ₦${amount.toLocaleString()} to ${detailUser.name}`);
    } catch (err) {
      alert(err.response?.data?.message || "Failed to credit user");
    } finally {
      setDetailActionLoading(false);
    }
  };

  const handleDetailDebitConfirmed = async () => {
    const amount = Number(detailDebitAmount);
    if (!amount || amount <= 0) return alert("Enter a valid amount to debit.");
    setDetailActionLoading(true);
    try {
      await AdminAPI.post("/debit", { email: detailUser.email, amount });
      setDetailDebitAmount("");
      setDebitConfirmOpen(false);
      await refreshDetailUser();
      fetchUsers();
      alert(`✅ Debited ₦${amount.toLocaleString()} from ${detailUser.name}`);
    } catch (err) {
      alert(err.response?.data?.message || "Failed to debit user");
    } finally {
      setDetailActionLoading(false);
    }
  };

  const handleResetPassword = async () => {
    if (!detailUser?._id) return;
    setDetailActionLoading(true);
    try {
      const res = await AdminAPI.post(`/users/${detailUser._id}/reset-password`);
      setResetPasswordResult(res.data.tempPassword);
    } catch (err) {
      alert(err.response?.data?.message || "Failed to reset password");
    } finally {
      setDetailActionLoading(false);
    }
  };

  const handleDeleteConfirmed = async () => {
    if (!detailUser?._id) return;
    setDetailActionLoading(true);
    try {
      await AdminAPI.delete(`/users/${detailUser._id}`);
      setDeleteConfirmOpen(false);
      closeUserDetail();
      fetchUsers();
    } catch (err) {
      alert(err.response?.data?.message || "Failed to delete user");
    } finally {
      setDetailActionLoading(false);
    }
  };

  const handleActivateSubscription = async () => {
    if (!detailUser?._id) return;

    const currentBalance = detailUser.balance || 0;
    const confirmed = window.confirm(
      `Activate ${detailUser.plan} subscription for ${detailUser.name}?\n\n` +
        `⚠️ This will ALSO reset their current balance (₦${currentBalance.toLocaleString()}) to ₦0. This cannot be undone.\n\n` +
        `Continue?`
    );
    if (!confirmed) return;

    setDetailActionLoading(true);
    try {
      const res = await AdminAPI.patch(`/users/${detailUser._id}/activate-subscription`);
      alert(res.data.message);
      await refreshDetailUser();
      fetchUsers();
    } catch (err) {
      alert(err.response?.data?.message || "Failed to activate subscription");
    } finally {
      setDetailActionLoading(false);
    }
  };

  // ✅ NEW: reverses an active subscription — subscriptionActive → false,
  // status → "pending". Does not touch balance. Opens a confirmation
  // modal first since this removes a paying user's access.
  const handleDeactivateSubscriptionConfirmed = async () => {
    if (!detailUser?._id) return;
    setDetailActionLoading(true);
    try {
      const res = await AdminAPI.patch(`/users/${detailUser._id}/deactivate-subscription`);
      alert(res.data.message);
      setDeactivateConfirmOpen(false);
      await refreshDetailUser();
      fetchUsers();
    } catch (err) {
      alert(err.response?.data?.message || "Failed to deactivate subscription");
    } finally {
      setDetailActionLoading(false);
    }
  };

  const handleChangePlan = async (newPlan) => {
    if (!detailUser?._id) return;
    const confirmed = window.confirm(
      `Change ${detailUser.name}'s plan from ${detailUser.plan || "none"} to ${newPlan}?\n\n` +
        `This does NOT change their current subscription status — only which plan they're assigned to.`
    );
    if (!confirmed) return;

    setDetailActionLoading(true);
    try {
      const res = await AdminAPI.patch(`/users/${detailUser._id}/change-plan`, { plan: newPlan });
      alert(res.data.message);
      await refreshDetailUser();
      fetchUsers();
    } catch (err) {
      alert(err.response?.data?.message || "Failed to change plan");
    } finally {
      setDetailActionLoading(false);
    }
  };

  const handleCleanupSoftDeleted = async () => {
    const confirmed = window.confirm(
      "This will PERMANENTLY delete every user you previously deleted (soft-deleted) from the database.\n\n" +
        "This cannot be undone. Continue?"
    );
    if (!confirmed) return;

    setCleanupLoading(true);
    try {
      const res = await AdminAPI.delete("/users/cleanup-soft-deleted");
      alert(res.data.message);
      setCleanupDone(true);
      fetchUsers();
    } catch (err) {
      alert(err.response?.data?.message || "Cleanup failed");
    } finally {
      setCleanupLoading(false);
    }
  };

  // ✅ toggle a single user's checkbox
  const toggleUserSelected = (userId) => {
    setSelectedUserIds((prev) =>
      prev.includes(userId) ? prev.filter((id) => id !== userId) : [...prev, userId]
    );
  };

  // ✅ select/deselect all currently-visible (filtered) users
  const toggleSelectAll = () => {
    if (selectedUserIds.length === filteredUsers.length) {
      setSelectedUserIds([]);
    } else {
      setSelectedUserIds(filteredUsers.map((u) => u._id));
    }
  };

  // ✅ the actual bulk-delete API call, fired only after confirmation
  const handleBulkDeleteConfirmed = async () => {
    setBulkDeleteLoading(true);
    try {
      const res = await AdminAPI.post("/users/bulk-delete", { userIds: selectedUserIds });
      alert(res.data.message);
      setSelectedUserIds([]);
      setBulkDeleteConfirmOpen(false);
      fetchUsers();
    } catch (err) {
      alert(err.response?.data?.message || "Bulk delete failed");
    } finally {
      setBulkDeleteLoading(false);
    }
  };

  const filteredUsers = users.filter((u) => {
    if (!searchQuery.trim()) return true;
    const q = searchQuery.trim().toLowerCase();
    return (
      (u.name || "").toLowerCase().includes(q) ||
      (u.email || "").toLowerCase().includes(q) ||
      (u.plan || "").toLowerCase().includes(q)
    );
  });

  // ✅ full user objects for the ones currently selected, used to
  // show names/emails in the confirmation modal
  const selectedUsersForConfirm = users.filter((u) => selectedUserIds.includes(u._id));

  if (loading) return <p>Loading users...</p>;

  return (
    <div className="space-y-6">
      {!cleanupDone && (
        <div className="bg-orange-50 border border-orange-200 p-4 rounded-xl shadow flex items-center justify-between flex-wrap gap-3">
          <div>
            <p className="font-semibold text-orange-900 text-sm">🧹 One-time cleanup available</p>
            <p className="text-xs text-orange-700 mt-0.5">
              Permanently remove all previously soft-deleted users from the database. This does NOT affect currently pending users.
            </p>
          </div>
          <button
            onClick={handleCleanupSoftDeleted}
            disabled={cleanupLoading}
            className="bg-orange-600 hover:bg-orange-700 disabled:opacity-50 text-white px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap"
          >
            {cleanupLoading ? "Cleaning up..." : "Run Cleanup"}
          </button>
        </div>
      )}

      <div className="bg-white p-6 rounded-xl shadow">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-bold">
            👑 VIP Tickets
            {tickets.length > 0 && (
              <span className="ml-2 inline-flex items-center justify-center bg-yellow-600 text-white text-xs font-bold rounded-full px-2 py-0.5">
                {tickets.length}
              </span>
            )}
          </h2>
        </div>

        {ticketsLoading ? (
          <p className="text-gray-500">Loading tickets...</p>
        ) : tickets.length === 0 ? (
          <p className="text-gray-500">No open VIP tickets right now.</p>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {tickets.map((t) => (
              <div
                key={t._id}
                className={`border rounded-xl p-4 flex flex-col gap-2 hover:shadow-md transition ${
                  t.priority === "urgent"
                    ? "border-red-300 bg-red-50"
                    : t.priority === "high"
                    ? "border-orange-300 bg-orange-50"
                    : "border-gray-200"
                }`}
              >
                <div className="flex items-center justify-between">
                  <span
                    className={`text-xs font-bold uppercase px-2 py-0.5 rounded-full ${
                      t.priority === "urgent"
                        ? "bg-red-600 text-white"
                        : t.priority === "high"
                        ? "bg-orange-500 text-white"
                        : "bg-gray-200 text-gray-700"
                    }`}
                  >
                    {t.priority}
                  </span>
                  <span className="text-xs text-gray-400">{new Date(t.createdAt).toLocaleString()}</span>
                </div>

                <p className="font-semibold text-sm">{t.subject}</p>
                <p className="text-xs text-gray-500">
                  {t.userName} · {t.userEmail} · <span className="capitalize">{t.userPlan}</span> plan
                </p>
                <p className="text-sm text-gray-700 line-clamp-3">{t.message}</p>

                <div className="flex gap-2 mt-1">
                  <button
                    onClick={() => resolveTicket(t._id)}
                    disabled={ticketActionId === t._id}
                    className="flex-1 bg-green-600 hover:bg-green-700 disabled:opacity-50 text-white px-3 py-1.5 rounded text-sm font-medium"
                  >
                    {ticketActionId === t._id ? "Working..." : "✅ Resolve"}
                  </button>
                  <button
                    onClick={() => setTicketReplyModal({ open: true, ticketId: t._id })}
                    disabled={ticketActionId === t._id}
                    className="flex-1 bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 text-white px-3 py-1.5 rounded text-sm font-medium"
                  >
                    Reply & Resolve
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="bg-white p-6 rounded-xl shadow">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-bold">
            📸 Pending Payment Proofs
            {proofs.length > 0 && (
              <span className="ml-2 inline-flex items-center justify-center bg-red-600 text-white text-xs font-bold rounded-full px-2 py-0.5">
                {proofs.length}
              </span>
            )}
          </h2>
        </div>

        {proofsLoading ? (
          <p className="text-gray-500">Loading proofs...</p>
        ) : proofs.length === 0 ? (
          <p className="text-gray-500">No pending proofs right now.</p>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {proofs.map((p) => (
              <div
                key={p._id}
                className="border rounded-xl p-4 flex flex-col gap-2 hover:shadow-md transition"
              >
                <img
                  src={p.screenshotUrl}
                  alt="Payment proof"
                  onClick={() => setSelectedProof(p.screenshotUrl)}
                  className="w-full h-40 object-cover rounded-lg border cursor-pointer"
                />

                <div className="text-sm space-y-0.5">
                  <p className="font-semibold">{p.name}</p>
                  <p className="text-gray-500">{p.email}</p>
                  <p>
                    <span className="text-gray-500">Amount: </span>
                    <span className="font-medium">{p.amountPaid}</span>
                  </p>
                  {p.bankUsed && (
                    <p>
                      <span className="text-gray-500">Bank: </span>
                      {p.bankUsed}
                    </p>
                  )}
                  {p.plan && (
                    <p>
                      <span className="text-gray-500">Plan: </span>
                      {p.plan}
                    </p>
                  )}
                  <p className="text-xs text-gray-400">
                    Submitted {new Date(p.createdAt).toLocaleString()}
                  </p>
                </div>

                <div className="flex gap-2 mt-1">
                  <button
                    onClick={() => approveProof(p._id)}
                    disabled={proofActionId === p._id}
                    className="flex-1 bg-green-600 hover:bg-green-700 disabled:opacity-50 text-white px-3 py-1.5 rounded text-sm font-medium"
                  >
                    {proofActionId === p._id ? "Working..." : "✅ Approve"}
                  </button>
                  <button
                    onClick={() => setProofRejectModal({ open: true, proofId: p._id })}
                    disabled={proofActionId === p._id}
                    className="flex-1 bg-red-600 hover:bg-red-700 disabled:opacity-50 text-white px-3 py-1.5 rounded text-sm font-medium"
                  >
                    Reject
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="bg-white p-6 rounded-xl shadow">
        <h2 className="text-xl font-bold mb-4">👥 Manage Users</h2>
        <p className="text-xs text-gray-400 mb-3">Tap a row to view full details, credit/debit, reset password, view upline/downline, or approve pending coin purchases. Use checkboxes to select multiple users for bulk deletion.</p>

        <div className="mb-4 relative">
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search by name, email, or plan..."
            className="w-full border rounded-lg p-2.5 pl-9 text-sm"
          />
          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm">🔍</span>
          {searchQuery && (
            <button
              onClick={() => setSearchQuery("")}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 text-sm"
              aria-label="Clear search"
            >
              ✕
            </button>
          )}
        </div>
        {searchQuery && (
          <p className="text-xs text-gray-400 mb-3">
            Showing {filteredUsers.length} of {users.length} users
          </p>
        )}

        {/* ✅ bulk-delete action bar, only shows when something's selected */}
        {selectedUserIds.length > 0 && (
          <div className="mb-4 p-3 rounded-lg bg-red-50 border border-red-200 flex items-center justify-between flex-wrap gap-2">
            <p className="text-sm text-red-800 font-medium">
              {selectedUserIds.length} user{selectedUserIds.length > 1 ? "s" : ""} selected
            </p>
            <div className="flex gap-2">
              <button
                onClick={() => setSelectedUserIds([])}
                className="px-3 py-1.5 rounded-lg text-sm font-medium bg-gray-200 hover:bg-gray-300"
              >
                Clear
              </button>
              <button
                onClick={() => setBulkDeleteConfirmOpen(true)}
                className="px-3 py-1.5 rounded-lg text-sm font-medium bg-red-600 hover:bg-red-700 text-white"
              >
                🗑️ Delete Selected
              </button>
            </div>
          </div>
        )}

        {filteredUsers.length === 0 ? (
          <p className="text-gray-500">
            {searchQuery ? "No users match your search." : "No users found."}
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full border">
              <thead>
                <tr className="bg-gray-200 text-left">
                  {/* ✅ select-all checkbox */}
                  <th className="p-2 w-8">
                    <input
                      type="checkbox"
                      checked={filteredUsers.length > 0 && selectedUserIds.length === filteredUsers.length}
                      onChange={toggleSelectAll}
                    />
                  </th>
                  <th className="p-2">Name</th>
                  <th className="p-2">Email</th>
                  <th className="p-2">Plan</th>
                  <th className="p-2">Balance</th>
                  <th className="p-2">Locked?</th>
                  <th className="p-2">Verified</th>
                  <th className="p-2">Pending Orders</th>
                  <th className="p-2">Action</th>
                </tr>
              </thead>
              <tbody>
                {filteredUsers.map((u) => (
                  <tr
                    key={u._id}
                    className={`border-b hover:bg-gray-50 cursor-pointer ${
                      selectedUserIds.includes(u._id) ? "bg-red-50" : ""
                    }`}
                    onClick={() => openUserDetail(u._id)}
                  >
                    {/* ✅ per-row checkbox */}
                    <td className="p-2" onClick={(e) => e.stopPropagation()}>
                      <input
                        type="checkbox"
                        checked={selectedUserIds.includes(u._id)}
                        onChange={() => toggleUserSelected(u._id)}
                      />
                    </td>
                    <td className="p-2 font-medium text-blue-700 hover:underline">{u.name}</td>
                    <td className="p-2">{u.email}</td>
                    <td className="p-2 capitalize">{u.plan || "—"}</td>
                    <td className="p-2">₦{u.balance?.toLocaleString() || "0"}</td>
                    <td className="p-2">
                      {u.isBalanceLocked ? (
                        <span className="text-red-600 font-semibold">🔒 Locked</span>
                      ) : (
                        <span className="text-green-600 font-semibold">🔓 Unlocked</span>
                      )}
                    </td>
                    <td className="p-2">
                      {u.accountLocked ? (
                        <span className="px-2 py-1 rounded text-sm bg-red-600 text-white font-semibold">
                          🚫 deactivated
                        </span>
                      ) : (
                        <span
                          className={`px-2 py-1 rounded text-sm ${
                            u.status === "active"
                              ? "bg-green-200"
                              : u.status === "rejected"
                              ? "bg-red-200"
                              : "bg-yellow-200"
                          }`}
                        >
                          {u.status || "pending"}
                        </span>
                      )}
                    </td>
                    <td className="p-2">
                      {pendingOrderCounts[u._id] > 0 ? (
                        <span className="inline-flex items-center justify-center bg-orange-500 text-white text-xs font-bold rounded-full px-2.5 py-1">
                          {pendingOrderCounts[u._id]} pending
                        </span>
                      ) : (
                        <span className="text-gray-300 text-xs">—</span>
                      )}
                    </td>
                    <td className="p-2 space-x-2" onClick={(e) => e.stopPropagation()}>
                      {u.status === "pending" && (
                        <>
                          <button
                            onClick={() => verifyUser(u._id)}
                            className="bg-green-600 text-white px-3 py-1 rounded text-sm"
                          >
                            Approve
                          </button>
                          <button
                            onClick={() => setRejectModal({ open: true, userId: u._id })}
                            className="bg-red-600 text-white px-3 py-1 rounded text-sm"
                          >
                            Reject
                          </button>
                        </>
                      )}
                      <button
                        onClick={() => toggleLockBalance(u._id, u.isBalanceLocked)}
                        className={`${
                          u.isBalanceLocked
                            ? "bg-yellow-500 hover:bg-yellow-600"
                            : "bg-blue-600 hover:bg-blue-700"
                        } text-white px-3 py-1 rounded text-sm`}
                      >
                        {u.isBalanceLocked ? "Unlock" : "Lock"}
                      </button>
                      <button
                        onClick={() => toggleAccountLock(u._id, u.accountLocked)}
                        className={`${
                          u.accountLocked
                            ? "bg-green-600 hover:bg-green-700"
                            : "bg-gray-800 hover:bg-black"
                        } text-white px-3 py-1 rounded text-sm`}
                        title={u.accountLocked ? "Reactivate this account" : "Deactivate this account — blocks login"}
                      >
                        {u.accountLocked ? "Reactivate" : "Deactivate"}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {(detailUser || detailLoading) && (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex items-start sm:items-center justify-center z-50 p-4 overflow-y-auto">
          <div className="bg-white rounded-xl shadow-lg max-w-2xl w-full my-8">
            {detailLoading && !detailUser ? (
              <div className="p-8 text-center text-gray-500">Loading user detail...</div>
            ) : (
              <>
                <div className="p-5 border-b flex items-start justify-between">
                  <div>
                    <h3 className="text-xl font-bold">{detailUser.name}</h3>
                    <p className="text-gray-500 text-sm">{detailUser.email}</p>
                  </div>
                  <button
                    onClick={closeUserDetail}
                    className="text-gray-400 hover:text-gray-700 text-xl leading-none"
                    aria-label="Close"
                  >
                    ✕
                  </button>
                </div>

                <div className="p-5 space-y-5 max-h-[70vh] overflow-y-auto">
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 text-sm">
                    <div className="bg-gray-50 rounded-lg p-3">
                      <p className="text-gray-400 text-xs uppercase">Plan</p>
                      <p className="font-semibold capitalize">{detailUser.plan || "—"}</p>
                    </div>
                    <div className="bg-gray-50 rounded-lg p-3">
                      <p className="text-gray-400 text-xs uppercase">Balance</p>
                      <p className="font-semibold">₦{detailUser.balance?.toLocaleString() || "0"}</p>
                    </div>
                    <div className="bg-gray-50 rounded-lg p-3">
                      <p className="text-gray-400 text-xs uppercase">Status</p>
                      <p className="font-semibold capitalize">{detailUser.status || "pending"}</p>
                    </div>
                    <div className="bg-gray-50 rounded-lg p-3 col-span-2 sm:col-span-1">
                      <p className="text-gray-400 text-xs uppercase">Registered</p>
                      <p className="font-semibold">
                        {detailUser.createdAt
                          ? new Date(detailUser.createdAt).toLocaleDateString()
                          : "—"}
                      </p>
                    </div>
                    <div className="bg-gray-50 rounded-lg p-3">
                      <p className="text-gray-400 text-xs uppercase">Downline count</p>
                      <p className="font-semibold">{detailUser.referrals?.length || 0}</p>
                    </div>
                    <div className="bg-gray-50 rounded-lg p-3">
                      <p className="text-gray-400 text-xs uppercase">Referral code</p>
                      <p className="font-semibold">{detailUser.referralCode || "—"}</p>
                    </div>
                  </div>

                  <div>
                    <p className="text-xs font-semibold uppercase text-gray-500 mb-1">Upline (referred by)</p>
                    {detailUser.referredBy ? (
                      <p className="text-sm bg-gray-50 rounded-lg p-3">
                        {detailUser.referredBy.name}{" "}
                        <span className="text-gray-400">({detailUser.referredBy.email})</span>
                      </p>
                    ) : (
                      <p className="text-sm text-gray-400 bg-gray-50 rounded-lg p-3">No upline — registered directly</p>
                    )}
                  </div>

                  <div>
                    <p className="text-xs font-semibold uppercase text-gray-500 mb-1">
                      Downline ({detailUser.referrals?.length || 0})
                    </p>
                    {detailUser.referrals && detailUser.referrals.length > 0 ? (
                      <div className="bg-gray-50 rounded-lg p-3 max-h-40 overflow-y-auto space-y-1.5">
                        {detailUser.referrals.map((r) => (
                          <p key={r._id} className="text-sm">
                            {r.name} <span className="text-gray-400">({r.email})</span>
                          </p>
                        ))}
                      </div>
                    ) : (
                      <p className="text-sm text-gray-400 bg-gray-50 rounded-lg p-3">No downline yet</p>
                    )}
                  </div>

                  <div>
                    <p className="text-xs font-semibold uppercase text-gray-500 mb-2">
                      Coin Purchase Orders {userOrders.length > 0 && `(${userOrders.length})`}
                    </p>
                    {userOrdersLoading ? (
                      <p className="text-sm text-gray-400 bg-gray-50 rounded-lg p-3">Loading orders...</p>
                    ) : userOrders.length === 0 ? (
                      <p className="text-sm text-gray-400 bg-gray-50 rounded-lg p-3">No purchase orders yet</p>
                    ) : (
                      <div className="space-y-2 max-h-64 overflow-y-auto">
                        {userOrders.map((order) => (
                          <div
                            key={order._id}
                            className={`rounded-lg p-3 text-sm border ${
                              order.status === "pending"
                                ? "bg-orange-50 border-orange-200"
                                : "bg-gray-50 border-gray-200"
                            }`}
                          >
                            <div className="flex items-center justify-between mb-1">
                              <span className="font-semibold">
                                {Number(order.amount).toLocaleString()} coins
                              </span>
                              <span
                                className={`text-xs font-bold px-2 py-0.5 rounded-full ${
                                  order.status === "pending"
                                    ? "bg-orange-200 text-orange-800"
                                    : order.status === "confirmed"
                                    ? "bg-green-200 text-green-800"
                                    : "bg-gray-200 text-gray-700"
                                }`}
                              >
                                {order.status}
                              </span>
                            </div>
                            <p className="text-gray-500 text-xs">
                              Vendor: {order.vendor?.name || "Unknown"} · Paid: ₦{Number(order.moneyPaid || 0).toLocaleString()}
                            </p>
                            <p className="text-gray-400 text-xs mt-0.5">
                              {new Date(order.createdAt).toLocaleString()}
                            </p>
                            {order.status === "pending" && (
                              <button
                                onClick={() => approveOrder(order._id)}
                                disabled={orderActionId === order._id}
                                className="mt-2 w-full bg-green-600 hover:bg-green-700 disabled:opacity-50 text-white px-3 py-1.5 rounded text-xs font-medium"
                              >
                                {orderActionId === order._id ? "Approving..." : "✅ Approve Order (bypass vendor)"}
                              </button>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  <div>
                    <p className="text-xs font-semibold uppercase text-gray-500 mb-2">Account Access</p>
                    {detailUser.accountLocked ? (
                      <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm mb-2">
                        <p className="font-semibold text-red-800">🚫 Account Deactivated</p>
                        <p className="text-red-700 mt-1">
                          Reason shown at login: {detailUser.accountLockReason || "No reason recorded"}
                        </p>
                      </div>
                    ) : (
                      <div className="bg-green-50 border border-green-200 rounded-lg p-3 text-sm mb-2">
                        <p className="font-semibold text-green-800">✅ Account Active — user can log in</p>
                      </div>
                    )}
                    <button
                      onClick={() =>
                        toggleAccountLock(detailUser._id, detailUser.accountLocked, true)
                      }
                      className={`${
                        detailUser.accountLocked
                          ? "bg-green-600 hover:bg-green-700"
                          : "bg-gray-800 hover:bg-black"
                      } text-white px-4 py-2 rounded-lg text-sm font-medium`}
                    >
                      {detailUser.accountLocked ? "Reactivate Account" : "🚫 Deactivate Account"}
                    </button>
                  </div>

                  <div>
                    <p className="text-xs font-semibold uppercase text-gray-500 mb-2">Balance Lock</p>
                    {detailUser.isBalanceLocked ? (
                      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 text-sm mb-2">
                        <p className="font-semibold text-yellow-800">🔒 Balance Locked</p>
                        <p className="text-yellow-700 mt-1">
                          Reason: {detailUser.lockReason || "No reason recorded"}
                        </p>
                      </div>
                    ) : (
                      <div className="bg-green-50 border border-green-200 rounded-lg p-3 text-sm mb-2">
                        <p className="font-semibold text-green-800">🔓 Balance Unlocked</p>
                      </div>
                    )}
                    <button
                      onClick={() =>
                        toggleLockBalance(detailUser._id, detailUser.isBalanceLocked, true)
                      }
                      className={`${
                        detailUser.isBalanceLocked
                          ? "bg-yellow-500 hover:bg-yellow-600"
                          : "bg-blue-600 hover:bg-blue-700"
                      } text-white px-4 py-2 rounded-lg text-sm font-medium`}
                    >
                      {detailUser.isBalanceLocked ? "Unlock Balance" : "Lock Balance"}
                    </button>
                  </div>

                  <div>
                    <p className="text-xs font-semibold uppercase text-gray-500 mb-2">Subscription Status</p>
                    {detailUser.subscriptionActive ? (
                      <>
                        <div className="bg-green-50 border border-green-200 rounded-lg p-3 text-sm mb-2">
                          <p className="font-semibold text-green-800">
                            ✅ Active — {detailUser.plan} dashboard unlocked
                          </p>
                        </div>
                        {/* ✅ NEW: reverse of activate — deactivate an active subscription */}
                        <button
                          onClick={() => setDeactivateConfirmOpen(true)}
                          disabled={detailActionLoading}
                          className="bg-red-600 hover:bg-red-700 disabled:opacity-50 text-white px-4 py-2 rounded-lg text-sm font-medium"
                        >
                          🔒 Deactivate {detailUser.plan || ""} Subscription
                        </button>
                      </>
                    ) : (
                      <>
                        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 text-sm mb-2">
                          <p className="font-semibold text-yellow-800">
                            ⚠️ Not Active — plan: {detailUser.plan || "none set"}
                          </p>
                          <p className="text-yellow-700 mt-1">
                            This user will be redirected to a demo dashboard on login until this is activated.
                          </p>
                        </div>
                        <button
                          onClick={handleActivateSubscription}
                          disabled={detailActionLoading || !detailUser.plan}
                          className="bg-green-600 hover:bg-green-700 disabled:opacity-50 text-white px-4 py-2 rounded-lg text-sm font-medium"
                        >
                          ✅ Activate {detailUser.plan || ""} Subscription
                        </button>
                      </>
                    )}
                  </div>

                  <div>
                    <p className="text-xs font-semibold uppercase text-gray-500 mb-2">Change Plan</p>
                    <p className="text-xs text-gray-400 mb-2">
                      Current: <span className="font-semibold capitalize">{detailUser.plan || "none"}</span>
                    </p>
                    <div className="grid grid-cols-3 gap-2">
                      {["basic", "premium", "elite"].map((planOption) => (
                        <button
                          key={planOption}
                          onClick={() => handleChangePlan(planOption)}
                          disabled={detailActionLoading || detailUser.plan === planOption}
                          className={`py-2 rounded-lg text-sm font-medium capitalize transition ${
                            detailUser.plan === planOption
                              ? "bg-gray-200 text-gray-400 cursor-not-allowed"
                              : "bg-indigo-600 hover:bg-indigo-700 text-white"
                          }`}
                        >
                          {planOption}
                        </button>
                      ))}
                    </div>
                    <p className="text-xs text-gray-400 mt-2">
                      This does not change their current subscription status — only which plan they're assigned to.
                    </p>
                  </div>

                  {detailUser.accountLocked && (
                    <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm">
                      <p className="font-semibold text-red-800">🚫 Full Account Locked</p>
                      <p className="text-red-700 mt-1">
                        Reason: {detailUser.accountLockReason || "No reason recorded"}
                      </p>
                    </div>
                  )}

                  {detailUser.status === "rejected" && detailUser.rejectionReason && (
                    <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm">
                      <p className="font-semibold text-red-800">❌ Rejected</p>
                      <p className="text-red-700 mt-1">Reason: {detailUser.rejectionReason}</p>
                    </div>
                  )}

                  <div>
                    <p className="text-xs font-semibold uppercase text-gray-500 mb-2">Credit / Debit</p>
                    <div className="flex gap-2 mb-3">
                      <button
                        onClick={() => setDetailMode("credit")}
                        className={`flex-1 py-2 rounded-lg text-sm font-medium ${
                          detailMode === "credit"
                            ? "bg-green-600 text-white"
                            : "bg-gray-100 text-gray-600"
                        }`}
                      >
                        💳 Credit
                      </button>
                      <button
                        onClick={() => setDetailMode("debit")}
                        className={`flex-1 py-2 rounded-lg text-sm font-medium ${
                          detailMode === "debit"
                            ? "bg-red-600 text-white"
                            : "bg-gray-100 text-gray-600"
                        }`}
                      >
                        💸 Debit
                      </button>
                    </div>

                    {detailMode === "credit" ? (
                      <div className="flex gap-2">
                        <input
                          type="number"
                          min="1"
                          placeholder="Amount to credit (₦)"
                          value={detailCreditAmount}
                          onChange={(e) => setDetailCreditAmount(e.target.value)}
                          className="flex-1 border rounded-lg p-2 text-sm"
                        />
                        <button
                          onClick={handleDetailCredit}
                          disabled={detailActionLoading}
                          className="bg-green-600 hover:bg-green-700 disabled:opacity-50 text-white px-4 rounded-lg text-sm font-medium"
                        >
                          Credit
                        </button>
                      </div>
                    ) : (
                      <div className="flex gap-2">
                        <input
                          type="number"
                          min="1"
                          placeholder="Amount to debit (₦)"
                          value={detailDebitAmount}
                          onChange={(e) => setDetailDebitAmount(e.target.value)}
                          className="flex-1 border rounded-lg p-2 text-sm"
                        />
                        <button
                          onClick={() => {
                            if (!detailDebitAmount || Number(detailDebitAmount) <= 0) {
                              return alert("Enter a valid amount to debit.");
                            }
                            setDebitConfirmOpen(true);
                          }}
                          disabled={detailActionLoading}
                          className="bg-red-600 hover:bg-red-700 disabled:opacity-50 text-white px-4 rounded-lg text-sm font-medium"
                        >
                          Debit
                        </button>
                      </div>
                    )}
                  </div>

                  <div>
                    <p className="text-xs font-semibold uppercase text-gray-500 mb-2">Password</p>
                    {resetPasswordResult ? (
                      <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                        <p className="text-sm text-blue-800 font-medium mb-1">
                          ✅ New temporary password (shown once — relay to user now):
                        </p>
                        <p className="font-mono text-lg font-bold text-blue-900 select-all">
                          {resetPasswordResult}
                        </p>
                      </div>
                    ) : (
                      <button
                        onClick={handleResetPassword}
                        disabled={detailActionLoading}
                        className="bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 text-white px-4 py-2 rounded-lg text-sm font-medium"
                      >
                        🔑 Reset Password (generate random)
                      </button>
                    )}
                  </div>

                  {detailUser.status === "pending" && (
                    <div className="pt-3 border-t">
                      <p className="text-xs font-semibold uppercase text-gray-500 mb-2">
                        Danger Zone
                      </p>
                      <button
                        onClick={() => setDeleteConfirmOpen(true)}
                        disabled={detailActionLoading}
                        className="bg-gray-700 hover:bg-gray-800 disabled:opacity-50 text-white px-4 py-2 rounded-lg text-sm font-medium"
                      >
                        🗑️ Permanently Delete Unpaid Registration
                      </button>
                      <p className="text-xs text-gray-400 mt-1">
                        Only available for pending users who never completed payment. This cannot be undone.
                      </p>
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {debitConfirmOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-[60] p-4">
          <div className="bg-white rounded-xl shadow-lg max-w-sm w-full p-6">
            <h3 className="text-lg font-bold mb-2">Confirm Debit</h3>
            <p className="text-sm text-gray-600 mb-4">
              You are about to debit{" "}
              <span className="font-semibold">₦{Number(detailDebitAmount).toLocaleString()}</span>{" "}
              from <span className="font-semibold">{detailUser?.name}</span>. This cannot be undone automatically.
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setDebitConfirmOpen(false)}
                className="flex-1 bg-gray-200 hover:bg-gray-300 py-2 rounded-lg text-sm font-medium"
              >
                Cancel
              </button>
              <button
                onClick={handleDetailDebitConfirmed}
                disabled={detailActionLoading}
                className="flex-1 bg-red-600 hover:bg-red-700 disabled:opacity-50 text-white py-2 rounded-lg text-sm font-medium"
              >
                {detailActionLoading ? "Processing..." : "Confirm Debit"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ✅ NEW: confirmation modal before deactivating a subscription —
          this removes a paying user's plan access, so it gets the same
          confirm-before-acting treatment as debit/delete. */}
      {deactivateConfirmOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-[60] p-4">
          <div className="bg-white rounded-xl shadow-lg max-w-sm w-full p-6">
            <h3 className="text-lg font-bold mb-2">Deactivate Subscription?</h3>
            <p className="text-sm text-gray-600 mb-4">
              This will deactivate{" "}
              <span className="font-semibold">{detailUser?.name}</span>'s{" "}
              <span className="font-semibold capitalize">{detailUser?.plan}</span> subscription
              and revert their account status back to <span className="font-semibold">pending</span>,
              as if it had never been approved. Their balance will not be affected.
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setDeactivateConfirmOpen(false)}
                className="flex-1 bg-gray-200 hover:bg-gray-300 py-2 rounded-lg text-sm font-medium"
              >
                Cancel
              </button>
              <button
                onClick={handleDeactivateSubscriptionConfirmed}
                disabled={detailActionLoading}
                className="flex-1 bg-red-600 hover:bg-red-700 disabled:opacity-50 text-white py-2 rounded-lg text-sm font-medium"
              >
                {detailActionLoading ? "Processing..." : "Confirm Deactivate"}
              </button>
            </div>
          </div>
        </div>
      )}

      {deleteConfirmOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-[60] p-4">
          <div className="bg-white rounded-xl shadow-lg max-w-sm w-full p-6">
            <h3 className="text-lg font-bold mb-2">Permanently Delete User?</h3>
            <p className="text-sm text-gray-600 mb-4">
              This will <span className="font-semibold text-red-600">permanently delete</span>{" "}
              <span className="font-semibold">{detailUser?.name}</span> ({detailUser?.email}) from the database.
              This <span className="font-semibold text-red-600">cannot be undone</span> — they will need to register again with a fresh account if this was a mistake.
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setDeleteConfirmOpen(false)}
                className="flex-1 bg-gray-200 hover:bg-gray-300 py-2 rounded-lg text-sm font-medium"
              >
                Cancel
              </button>
              <button
                onClick={handleDeleteConfirmed}
                disabled={detailActionLoading}
                className="flex-1 bg-red-700 hover:bg-red-800 disabled:opacity-50 text-white py-2 rounded-lg text-sm font-medium"
              >
                {detailActionLoading ? "Deleting..." : "Yes, Permanently Delete"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ✅ bulk-delete confirmation modal — lists every selected user
          by name and email before the irreversible action fires */}
      {bulkDeleteConfirmOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-[60] p-4">
          <div className="bg-white rounded-xl shadow-lg max-w-md w-full p-6">
            <h3 className="text-lg font-bold mb-2 text-red-700">
              Permanently Delete {selectedUsersForConfirm.length} User{selectedUsersForConfirm.length > 1 ? "s" : ""}?
            </h3>
            <p className="text-sm text-gray-600 mb-3">
              This <span className="font-semibold text-red-600">cannot be undone</span>. The following accounts will be permanently removed, regardless of status:
            </p>
            <div className="bg-gray-50 rounded-lg p-3 max-h-48 overflow-y-auto mb-4 space-y-1.5">
              {selectedUsersForConfirm.map((u) => (
                <p key={u._id} className="text-sm">
                  <span className="font-medium">{u.name}</span>{" "}
                  <span className="text-gray-400">({u.email})</span>{" "}
                  <span className="text-xs uppercase text-gray-400">— {u.status || "pending"}</span>
                </p>
              ))}
            </div>
            <div className="flex gap-3">
              <button
                onClick={() => setBulkDeleteConfirmOpen(false)}
                className="flex-1 bg-gray-200 hover:bg-gray-300 py-2 rounded-lg text-sm font-medium"
              >
                Cancel
              </button>
              <button
                onClick={handleBulkDeleteConfirmed}
                disabled={bulkDeleteLoading}
                className="flex-1 bg-red-700 hover:bg-red-800 disabled:opacity-50 text-white py-2 rounded-lg text-sm font-medium"
              >
                {bulkDeleteLoading ? "Deleting..." : `Yes, Delete ${selectedUsersForConfirm.length}`}
              </button>
            </div>
          </div>
        </div>
      )}

      {ticketReplyModal.open && (
        <div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-[60] p-4">
          <div className="bg-white rounded-xl shadow-lg max-w-md w-full p-6">
            <h3 className="text-lg font-bold mb-2">Reply & Resolve Ticket</h3>
            <textarea
              placeholder="Your reply to the user (optional)..."
              value={ticketReplyText}
              onChange={(e) => setTicketReplyText(e.target.value)}
              className="w-full border rounded-lg p-3 mb-4"
              rows="4"
            />
            <div className="flex gap-3">
              <button
                onClick={() => {
                  setTicketReplyModal({ open: false, ticketId: null });
                  setTicketReplyText("");
                }}
                className="flex-1 bg-gray-200 hover:bg-gray-300 py-2 rounded-lg text-sm font-medium"
              >
                Cancel
              </button>
              <button
                onClick={resolveTicketWithReply}
                disabled={ticketActionId === ticketReplyModal.ticketId}
                className="flex-1 bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 text-white py-2 rounded-lg text-sm font-medium"
              >
                {ticketActionId === ticketReplyModal.ticketId ? "Saving..." : "Resolve"}
              </button>
            </div>
          </div>
        </div>
      )}

      {selectedProof && (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50">
          <div className="bg-white p-4 rounded-xl shadow-lg max-w-lg w-full">
            <h3 className="text-lg font-bold mb-2">Payment Proof</h3>
            <img
              src={selectedProof}
              alt="Payment Proof"
              className="w-full rounded-lg border mb-4"
            />
            <div className="flex justify-end">
              <button
                onClick={() => setSelectedProof(null)}
                className="bg-red-500 text-white px-4 py-2 rounded hover:bg-red-600"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {rejectModal.open && (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded-xl shadow-lg max-w-md w-full">
            <h3 className="text-lg font-bold mb-4">Reject User</h3>
            <textarea
              placeholder="Enter reason for rejection..."
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              className="w-full border rounded-lg p-3 mb-4"
              rows="3"
            />
            <div className="flex justify-between">
              <button
                onClick={() => setRejectModal({ open: false, userId: null })}
                className="bg-gray-400 text-white px-4 py-2 rounded"
              >
                Cancel
              </button>
              <button
                onClick={rejectUser}
                className="bg-red-600 text-white px-4 py-2 rounded hover:bg-red-700"
              >
                Reject User
              </button>
            </div>
          </div>
        </div>
      )}

      {proofRejectModal.open && (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded-xl shadow-lg max-w-md w-full">
            <h3 className="text-lg font-bold mb-4">Reject Payment Proof</h3>
            <textarea
              placeholder="Optional note (e.g. screenshot unclear, amount mismatch)..."
              value={proofRejectNote}
              onChange={(e) => setProofRejectNote(e.target.value)}
              className="w-full border rounded-lg p-3 mb-4"
              rows="3"
            />
            <div className="flex justify-between">
              <button
                onClick={() => {
                  setProofRejectModal({ open: false, proofId: null });
                  setProofRejectNote("");
                }}
                className="bg-gray-400 text-white px-4 py-2 rounded"
              >
                Cancel
              </button>
              <button
                onClick={rejectProof}
                className="bg-red-600 text-white px-4 py-2 rounded hover:bg-red-700"
              >
                Reject Proof
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminUsers;
